import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, UserCheck, Edit3, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Managers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", position: "", salary: "" });

  const load = async () => setItems((await api.get("/managers")).data);
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditId(null);
    setForm({ name:"", position:"", salary:"" });
  };

  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (m) => {
    setEditId(m.id);
    setForm({ name: m.name, position: m.position, salary: String(m.salary ?? "") });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, salary: Number(form.salary) };
      if (editId) {
        await api.put(`/managers/${editId}`, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post("/managers", payload);
        toast.success("Çalışan eklendi");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Kaydı silmek istiyor musunuz?")) return;
    await api.delete(`/managers/${id}`);
    toast.success("Silindi"); load();
  };

  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return items;
    return items.filter(m =>
      (m.name || "").toLocaleLowerCase("tr").includes(q) ||
      (m.position || "").toLocaleLowerCase("tr").includes(q)
    );
  }, [items, search]);

  const total = items.reduce((s, m) => s + Number(m.salary || 0), 0);
  const filteredTotal = filtered.reduce((s, m) => s + Number(m.salary || 0), 0);
  const perCompany = total / 5;

  return (
    <div className="space-y-6" data-testid="managers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500">Çalışanlar (Yönetim)</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">10 Çalışan · Ortak Maaş Giderleri</h1>
          <p className="text-sm text-slate-400 mt-1">
            Ortak maaşları toplam gider olarak kaydedilir ve her ay <span className="text-amber-300 font-medium">5 firmaya %20 eşit</span> paylaştırılır.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <Input data-testid="mgr-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Ara: isim, pozisyon…"
                   className="w-[220px] pl-9 bg-slate-900/60 border-slate-700" />
          </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-manager-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="w-4 h-4 mr-1.5"/> Yeni Çalışan
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131b2e] border-white/10">
              <DialogHeader><DialogTitle>{editId ? "Kaydı Düzenle" : "Yeni Çalışan"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Ad Soyad</Label>
                  <Input required data-testid="mgr-name" value={form.name}
                         onChange={(e)=>setForm({...form, name:e.target.value})}
                         className="bg-slate-900/60 border-slate-700" />
                </div>
                <div className="space-y-2">
                  <Label>Pozisyon</Label>
                  <Input required data-testid="mgr-position" value={form.position}
                         onChange={(e)=>setForm({...form, position:e.target.value})}
                         className="bg-slate-900/60 border-slate-700" />
                </div>
                <div className="space-y-2">
                  <Label>Aylık Maaş (₺)</Label>
                  <Input required type="number" step="0.01" data-testid="mgr-salary" value={form.salary}
                         onChange={(e)=>setForm({...form, salary:e.target.value})}
                         className="bg-slate-900/60 border-slate-700" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="mgr-submit" className="bg-indigo-600 hover:bg-indigo-500">
                    {editId ? "Güncelle" : "Kaydet"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Ortak Sayısı</div>
          <div className="text-2xl font-semibold fk-mono mt-2">{items.length}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Aylık Toplam Maaş</div>
          <div className="text-2xl font-semibold fk-mono mt-2 text-amber-300">{formatTRY(total)}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Firma Başına Pay (%20)</div>
          <div className="text-2xl font-semibold fk-mono mt-2 text-indigo-300">{formatTRY(perCompany)}</div>
        </div>
      </div>

      <div className="fk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[.02] text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Ortak</th>
                <th className="text-left px-5 py-3 font-medium">Pozisyon</th>
                <th className="text-right px-5 py-3 font-medium">Maaş</th>
                {isAdmin && <th className="w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((m) => (
                <tr key={m.id} className="hover:bg-white/[.02]" data-testid={`mgr-row-${m.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                        <UserCheck className="w-4 h-4 text-indigo-300"/>
                      </div>
                      <span className="font-medium text-white">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-400">{m.position}</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-300">{formatTRY(m.salary)}</td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={()=>openEdit(m)}
                                data-testid={`edit-mgr-${m.id}`}
                                className="text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 h-8 w-8 p-0">
                          <Edit3 className="w-4 h-4"/>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>remove(m.id)}
                                data-testid={`del-mgr-${m.id}`}
                                className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4"/>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 4 : 3} className="px-5 py-10 text-center text-slate-500">
                  {search ? `"${search}" için kayıt bulunamadı` : "Henüz kayıt yok."}
                </td></tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-white/[.03] font-semibold border-t border-white/10">
                <tr>
                  <td className="px-5 py-3 text-slate-300 uppercase text-xs tracking-wider" colSpan={2}>Toplam ({filtered.length})</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-300" data-testid="mgr-total">{formatTRY(filteredTotal)}</td>
                  {isAdmin && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
